import React from 'react';
import { PlatformService } from '@lightning/services';
import { Carousel, CarouselProps } from './carousel';
import { MobileCarousel } from './mobile-carousel';
import { CarouselItem } from './carousel';
import { MobileCarouselItem } from './mobile-carousel-item';

/**
 * Adaptive carousel components that automatically use the appropriate
 * implementation based on the platform (native vs web)
 */

// Define explicit props type for the AdaptiveCarousel
type AdaptiveCarouselProps = React.HTMLAttributes<HTMLDivElement> & CarouselProps;

// Adaptive Carousel that chooses between regular and mobile implementations
export const AdaptiveCarousel: React.ForwardRefExoticComponent<
  AdaptiveCarouselProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, AdaptiveCarouselProps>((props, ref) => {
  if (PlatformService.isNative()) {
    return <MobileCarousel ref={ref} {...props} />;
  }
  return <Carousel ref={ref} {...props} />;
});

AdaptiveCarousel.displayName = 'AdaptiveCarousel';

// Adaptive Carousel Item
interface AdaptiveCarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  onItemClick?: () => void;
  preventClickOnSwipe?: boolean;
}

export const AdaptiveCarouselItem = React.forwardRef<
  HTMLDivElement,
  AdaptiveCarouselItemProps
>(({ onItemClick, preventClickOnSwipe = true, ...props }, ref) => {
  if (PlatformService.isNative() && onItemClick) {
    return (
      <MobileCarouselItem
        ref={ref}
        onItemClick={onItemClick}
        preventClickOnSwipe={preventClickOnSwipe}
        {...props}
      />
    );
  }
  
  // For web, use regular CarouselItem with click handler
  return (
    <CarouselItem 
      ref={ref} 
      {...props}
      onClick={onItemClick}
    />
  );
});

AdaptiveCarouselItem.displayName = 'AdaptiveCarouselItem';

// Export other carousel components for convenience
export { CarouselContent, CarouselNext, CarouselPrevious } from './carousel';